import type { AudioEngine } from "../host/engine";
import {
  createEngineSchedulerHost,
  isAudioEngine,
  type SchedulerHost,
} from "../host/schedulerHost";
import { deepClone } from "../kernel/deepClone";
import { normalizeFxNode } from "../kernel/fxChain";
import type { FxNode, InstrumentSlot } from "../types";
import { MAX_INSTRUMENT_SLOT_COUNT } from "../types";
import { decodeNoteFx } from "./noteFxDecode";
import { isCutOpenHold } from "./fx";
import { parseNoteName } from "./notes";
import {
  clipOrderSteps,
  createEmptyClip,
  locateOrderStep,
  secondsPerStep,
  type ClipIR,
  type NoteFx,
  type PatternCell,
} from "./types";

export type PlayheadInfo = {
  patternId: string;
  row: number;
};

export type PatternPlayerOptions = {
  /** How far ahead to schedule (seconds). */
  lookahead?: number;
  /** Scheduler poll interval (ms). */
  intervalMs?: number;
  /**
   * `timer` (default) — setInterval on the host thread.
   * `external` — caller drives `tick()` (AudioWorklet `process`).
   */
  drive?: "timer" | "external";
  /** Fired when the audible playhead changes (or `undefined` on stop). */
  onPlayhead?: (info: PlayheadInfo | undefined) => void;
  /**
   * When true (default), start/stop posts transport play/stop to the kernel.
   * Disable when a host (e.g. AudioSession) owns transport for multiple players.
   */
  manageTransport?: boolean;
  /**
   * When true (default), setClip/start posts setBpm from the clip.
   * Disable for SFX one-shots so they don't overwrite the music tempo.
   */
  syncBpm?: boolean;
  /**
   * When true (default), advance the direct-mode engine clock each tick.
   * Only one player per engine should do this.
   */
  advanceClock?: boolean;
  /** Fired when playback ends (stop, dispose, or one-shot finished). */
  onEnded?: () => void;
  /**
   * Override `Math.random` for `?xx` chance FX (returns 0..1).
   * Useful in tests for deterministic rolls.
   */
  random?: () => number;
};

type ScheduledKey = string;

/** Host-side repeating Gxy chops (used when cut domain is unbounded / C--). */
type ContinuousGate = {
  onset: number;
  period: number;
  onDur: number;
  velocity: number;
  nextIndex: number;
  keyPrefix: string;
  /** Stop scheduling opens at/after this time (set by --- / next note). */
  endAt?: number;
};

/**
 * Host-side pattern scheduler: walks Clip IR and posts timed noteOn/noteOff
 * into the engine transport (worklet or direct).
 */
export class PatternPlayer {
  private clip: ClipIR | undefined;
  private playing = false;
  private timer: ReturnType<typeof setInterval> | undefined;
  private playheadTimer: ReturnType<typeof setInterval> | undefined;
  private /** audio time when transport play began */ origin = 0;
  private /** next absolute step index across looped patterns */ nextStepIndex = 0;
  private scheduled = new Set<ScheduledKey>();
  private lookahead: number;
  private readonly intervalMs: number;
  private lastPlayheadKey: string | undefined;
  /** Pattern id of the last scheduled row (for mute cuts across boundaries). */
  private lastScheduledPatternId: string | undefined;
  /** Active gate closers per slot for overwrite */
  private gateUntil: Array<number | undefined> = Array.from(
    { length: MAX_INSTRUMENT_SLOT_COUNT },
    () => undefined,
  );
  /**
   * When true for a slot, omit the automatic ~1-step gate (like C--).
   * Explicit `Cxx` and `---` still schedule noteOff.
   */
  private openHoldDefaultSlots: boolean[] = Array.from(
    { length: MAX_INSTRUMENT_SLOT_COUNT },
    () => false,
  );
  /** Unbounded Gxy loops (C--); extended each scheduleAhead into the lookahead. */
  private continuousGates: Array<ContinuousGate | undefined> = Array.from(
    { length: MAX_INSTRUMENT_SLOT_COUNT },
    () => undefined,
  );

  private readonly host: SchedulerHost;
  private readonly manageTransport: boolean;
  private readonly syncBpm: boolean;
  private readonly advanceClock: boolean;
  private readonly drive: "timer" | "external";
  private readonly muted: boolean[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => false);
  private readonly solo: boolean[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => false);

  constructor(
    engineOrHost: AudioEngine | SchedulerHost,
    private readonly options: PatternPlayerOptions = {},
  ) {
    this.host = isAudioEngine(engineOrHost)
      ? createEngineSchedulerHost(engineOrHost)
      : engineOrHost;
    this.lookahead = options.lookahead ?? 0.5;
    this.intervalMs = options.intervalMs ?? 25;
    this.drive = options.drive ?? "timer";
    this.manageTransport = options.manageTransport ?? true;
    this.syncBpm = options.syncBpm ?? true;
    this.advanceClock = options.advanceClock ?? true;
  }

  /** Replace lookahead length (e.g. lab 0.25s) while idle or playing. */
  setLookahead(seconds: number): void {
    this.lookahead = Math.max(0.05, seconds);
  }

  isPlaying(): boolean {
    return this.playing;
  }

  getClip(): ClipIR | undefined {
    return this.clip;
  }

  /** Per-slot mute flags (length MAX_INSTRUMENT_SLOT_COUNT). */
  getMuted(): boolean[] {
    return [...this.muted];
  }

  /** Per-slot solo flags (length MAX_INSTRUMENT_SLOT_COUNT). */
  getSolo(): boolean[] {
    return [...this.solo];
  }

  setTrackMute(slot: InstrumentSlot, muted: boolean): void {
    if (this.muted[slot] === muted) {
      return;
    }
    this.muted[slot] = muted;
    this.onAudibilityChanged();
  }

  setTrackSolo(slot: InstrumentSlot, solo: boolean): void {
    if (this.solo[slot] === solo) {
      return;
    }
    this.solo[slot] = solo;
    this.onAudibilityChanged();
  }

  /** Replace mute/solo masks (lengths padded/truncated to MAX slots). */
  setMuteSolo(state: { muted?: boolean[]; solo?: boolean[] }): void {
    if (state.muted) {
      for (let i = 0; i < MAX_INSTRUMENT_SLOT_COUNT; i++) {
        this.muted[i] = Boolean(state.muted[i]);
      }
    }
    if (state.solo) {
      for (let i = 0; i < MAX_INSTRUMENT_SLOT_COUNT; i++) {
        this.solo[i] = Boolean(state.solo[i]);
      }
    }
    this.onAudibilityChanged();
  }

  /**
   * When a slot flag is true, notes without explicit `Cxx` omit the automatic
   * ~1-step gate (same as `C--`).
   */
  setOpenHoldDefaultSlots(flags: boolean[]): void {
    for (let i = 0; i < MAX_INSTRUMENT_SLOT_COUNT; i++) {
      this.openHoldDefaultSlots[i] = Boolean(flags[i]);
    }
  }

  getOpenHoldDefaultSlots(): boolean[] {
    return [...this.openHoldDefaultSlots];
  }

  /**
   * True when playback should emit notes for this slot.
   * `patternMuted` is the per-pattern track flag; `this.muted` is a global
   * override (stem bounce / legacy force-mute).
   */
  isTrackAudible(slot: InstrumentSlot, patternMuted = false): boolean {
    const anySolo = this.solo.some(Boolean);
    if (anySolo) {
      return this.solo[slot] === true;
    }
    return !this.muted[slot] && !patternMuted;
  }

  setClip(clip: ClipIR): void {
    // deepClone: AudioWorklet has no structuredClone in some browsers.
    this.clip = deepClone(clip);
    if (this.syncBpm) {
      this.host.setBpm(clip.bpm);
    }
    // Clip IR changes (cells, pattern mute, …) while playing must rebuild the
    // lookahead — otherwise queued noteOns keep firing for up to `lookahead`.
    if (this.playing) {
      this.resyncLookahead();
    }
  }

  start(clip?: ClipIR, options?: { startStep?: number }): void {
    if (clip) {
      this.setClip(clip);
    }
    if (!this.clip || this.clip.patterns.length === 0) {
      return;
    }
    this.stopTimerOnly();
    this.scheduled.clear();
    this.gateUntil = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => undefined);
    this.continuousGates = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => undefined);
    if (this.syncBpm) {
      this.host.setBpm(this.clip.bpm);
    }
    if (this.manageTransport) {
      this.host.play();
    }

    const totalSteps = clipOrderSteps(this.clip);
    const startStep =
      totalSteps <= 0
        ? 0
        : Math.max(0, Math.min(totalSteps - 1, Math.floor(options?.startStep ?? 0)));
    this.nextStepIndex = startStep;
    const stepSec = secondsPerStep(this.clip);
    // Backdate origin so elapsed time already maps to startStep.
    this.origin = this.now() - startStep * stepSec;
    this.playing = true;
    this.lastPlayheadKey = undefined;
    this.lastScheduledPatternId = undefined;
    this.scheduleAhead();
    this.emitPlayhead();
    if (this.drive === "timer") {
      this.timer = setInterval(() => {
        if (this.advanceClock && this.host.mode === "direct") {
          this.host.advanceTime?.(this.intervalMs / 1000);
        }
        this.scheduleAhead();
      }, this.intervalMs);
      // Playhead UI poll — independent of scheduler lookahead
      this.playheadTimer = setInterval(() => this.emitPlayhead(), 16);
    }
  }

  /**
   * Drive one schedule + playhead update (AudioWorklet `process`, tests).
   * No-op when not playing or when `drive: "timer"` owns the loop.
   */
  tick(): void {
    if (!this.playing) {
      return;
    }
    if (this.advanceClock && this.host.mode === "direct" && this.drive === "external") {
      // External drivers (offline render) advance time themselves before tick.
    }
    this.scheduleAhead();
    this.emitPlayhead();
  }

  stop(): void {
    const wasPlaying = this.playing;
    this.playing = false;
    this.stopTimerOnly();
    if (this.manageTransport) {
      this.host.stop();
      this.host.allNotesOff();
    }
    this.scheduled.clear();
    this.continuousGates = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => undefined);
    this.nextStepIndex = 0;
    this.lastPlayheadKey = undefined;
    this.lastScheduledPatternId = undefined;
    this.options.onPlayhead?.(undefined);
    if (wasPlaying) {
      this.options.onEnded?.();
    }
  }

  dispose(): void {
    this.stop();
  }

  private stopTimerOnly(): void {
    if (this.timer !== undefined) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
    if (this.playheadTimer !== undefined) {
      clearInterval(this.playheadTimer);
      this.playheadTimer = undefined;
    }
  }

  private now(): number {
    return this.host.getCurrentTime();
  }

  /** Playhead within the currently audible pattern, if any. */
  getPlayhead(): PlayheadInfo | undefined {
    if (!this.playing || !this.clip) {
      return undefined;
    }
    const totalStepsInOrder = clipOrderSteps(this.clip);
    if (totalStepsInOrder <= 0) {
      return undefined;
    }
    const stepSec = secondsPerStep(this.clip);
    if (stepSec <= 0) {
      return undefined;
    }
    const elapsed = this.now() - this.origin;
    const absoluteStep = elapsed < 0 ? 0 : Math.floor(elapsed / stepSec);
    if (!this.clip.loop && absoluteStep >= totalStepsInOrder) {
      return undefined;
    }
    const indexInCycle = ((absoluteStep % totalStepsInOrder) + totalStepsInOrder) % totalStepsInOrder;
    const located = locateOrderStep(this.clip, indexInCycle);
    if (!located) {
      return undefined;
    }
    return { patternId: located.pattern.id, row: located.row };
  }

  /** @deprecated Prefer getPlayhead(); returns row only. */
  getPlayheadRow(): number | undefined {
    return this.getPlayhead()?.row;
  }

  private emitPlayhead(): void {
    const info = this.getPlayhead();
    const key = info ? `${info.patternId}:${info.row}` : "";
    if (key === this.lastPlayheadKey) {
      return;
    }
    this.lastPlayheadKey = key;
    this.options.onPlayhead?.(info);
  }

  private scheduleAhead(): void {
    if (!this.playing || !this.clip) {
      return;
    }
    const clip = this.clip;
    const stepSec = secondsPerStep(clip);
    const horizon = this.now() + this.lookahead;
    const totalStepsInOrder = clipOrderSteps(clip);

    if (totalStepsInOrder === 0) {
      return;
    }

    while (true) {
      const stepTime = this.origin + this.nextStepIndex * stepSec;
      if (stepTime > horizon) {
        break;
      }

      const cycle = Math.floor(this.nextStepIndex / totalStepsInOrder);
      if (!clip.loop && cycle > 0) {
        // One-shot finished: release C-- / continuous gates at the pattern end.
        // Do not allNotesOff() here — that clears pending lookahead noteOns.
        // Keep continuousGates until after fillContinuousGates so C--+Gxy can
        // schedule remaining chops up to the boundary.
        this.releaseHoldsAt(stepTime);
        this.playing = false;
        this.stopTimerOnly();
        this.gateUntil = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => undefined);
        this.lastPlayheadKey = undefined;
        this.options.onPlayhead?.(undefined);
        if (this.manageTransport) {
          this.host.stop();
        }
        this.options.onEnded?.();
        break;
      }

      const indexInCycle = this.nextStepIndex % totalStepsInOrder;
      const located = locateOrderStep(clip, indexInCycle);
      if (located) {
        // When crossing into a pattern where a track is muted, cut any hold.
        const prevPatternId = this.lastScheduledPatternId;
        if (prevPatternId !== undefined && prevPatternId !== located.pattern.id) {
          this.cutInaudibleAt(stepTime, located.pattern);
        }
        this.lastScheduledPatternId = located.pattern.id;
        this.scheduleRow(located.pattern.tracks, located.row, stepTime, stepSec);
      }

      this.nextStepIndex += 1;
    }

    this.fillContinuousGates(horizon);
    if (!this.playing) {
      this.continuousGates = Array.from(
        { length: MAX_INSTRUMENT_SLOT_COUNT },
        () => undefined,
      );
      this.scheduled.clear();
    }
  }

  /** Extend unbounded C--+Gxy chops up to the schedule horizon. */
  private fillContinuousGates(horizon: number): void {
    for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
      const gate = this.continuousGates[slot];
      if (!gate) {
        continue;
      }
      // Cap slices per tick so a tiny period cannot runaway.
      const maxSlices = 256;
      let scheduled = 0;
      while (scheduled < maxSlices) {
        const tOn = gate.onset + gate.nextIndex * gate.period;
        if (tOn > horizon) {
          break;
        }
        if (gate.endAt !== undefined && tOn >= gate.endAt) {
          this.continuousGates[slot] = undefined;
          break;
        }
        const i = gate.nextIndex;
        if (i > 0) {
          const openKey = `${gate.keyPrefix}:gopen:${i}`;
          if (!this.scheduled.has(openKey)) {
            this.scheduled.add(openKey);
            this.host.setNoteFx(
              slot as InstrumentSlot,
              {},
              tOn,
              gate.velocity,
            );
          }
        }
        const tMute = tOn + gate.onDur;
        // Don't mute past note end — leave release to noteOff.
        if (gate.endAt === undefined || tMute < gate.endAt) {
          const muteKey = `${gate.keyPrefix}:gmute:${i}`;
          if (!this.scheduled.has(muteKey)) {
            this.scheduled.add(muteKey);
            this.host.setNoteFx(slot as InstrumentSlot, {}, tMute, 0);
          }
        }
        gate.nextIndex += 1;
        scheduled += 1;
      }
    }
  }

  private clearContinuousGate(slot: InstrumentSlot): void {
    this.continuousGates[slot] = undefined;
  }

  private endContinuousGate(slot: InstrumentSlot, atTime: number): void {
    const gate = this.continuousGates[slot];
    if (!gate) {
      return;
    }
    if (gate.endAt === undefined || atTime < gate.endAt) {
      gate.endAt = atTime;
    }
  }

  /**
   * Close open holds (C--) and continuous Gxy chops at `atTime`.
   * Used when a non-looped clip reaches its natural end — those notes never
   * received a host gate noteOff, so they would otherwise ring forever.
   */
  private releaseHoldsAt(atTime: number): void {
    const clip = this.clip;
    const slots = new Set<InstrumentSlot>();
    if (clip) {
      for (const pattern of clip.patterns) {
        for (const track of pattern.tracks) {
          slots.add(track.slot);
        }
      }
    }
    for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
      if (this.continuousGates[slot]) {
        slots.add(slot as InstrumentSlot);
      }
    }
    for (const slot of slots) {
      this.endContinuousGate(slot, atTime);
      const key = `oneshot-end:${atTime}:${slot}`;
      if (this.scheduled.has(key)) {
        continue;
      }
      this.scheduled.add(key);
      this.host.noteOff(slot, undefined, atTime);
    }
  }

  private scheduleRow(
    tracks: ClipIR["patterns"][0]["tracks"],
    row: number,
    stepTime: number,
    stepSec: number,
  ): void {
    for (const track of tracks) {
      const cell = track.cells[row] ?? {};
      this.scheduleCell(track.slot, cell, stepTime, stepSec, row, track.muted === true);
    }
  }

  private scheduleCell(
    slot: InstrumentSlot,
    cell: PatternCell,
    stepTime: number,
    stepSec: number,
    row: number,
    patternMuted = false,
  ): void {
    if (!this.isTrackAudible(slot, patternMuted)) {
      return;
    }
    const keyBase = `${slot}:${this.nextStepIndex}:${row}`;
    const { params } = decodeNoteFx(cell.fx, { stepSec });
    const hasKernelFx =
      params.vibratoSpeedHz !== undefined
      || params.vibratoDepthSemi !== undefined
      || params.slideSemiPerSec !== undefined
      || params.portaSemiPerSec !== undefined
      || params.cutoffOct !== undefined
      || params.arpHz !== undefined
      || params.arpSemi1 !== undefined
      || params.arpSemi2 !== undefined
      || params.pan !== undefined;

    if (cell.noteOff) {
      const key = `${keyBase}:off`;
      if (!this.scheduled.has(key)) {
        this.scheduled.add(key);
        this.endContinuousGate(slot, stepTime);
        this.host.noteOff(slot, undefined, stepTime);
        this.gateUntil[slot] = undefined;
      }
      return;
    }

    if (cell.note === undefined) {
      // FX-only / Vol-only row — patch the currently sounding voice (no new noteOn).
      const volPatch = cell.volume;
      if (hasKernelFx || volPatch !== undefined) {
        const key = `${keyBase}:fx`;
        if (!this.scheduled.has(key)) {
          this.scheduled.add(key);
          this.host.setNoteFx(slot, params, stepTime, volPatch);
        }
      }
      return;
    }

    const burstKey = `${keyBase}:burst`;
    if (this.scheduled.has(burstKey)) {
      return;
    }

    // ?xx — decimal percent chance this pitched note (and its R/G burst) plays.
    const chanceFx = cell.fx?.find(fx => fx.kind === "chance");
    if (chanceFx) {
      const pct = Math.min(99, Math.max(0, chanceFx.value | 0));
      const roll = (this.options.random ?? Math.random)();
      if (pct <= 0 || roll * 100 >= pct) {
        this.scheduled.add(burstKey);
        return;
      }
    }
    this.scheduled.add(burstKey);

    const velocity = cell.volume ?? 1;
    const noteFx = hasKernelFx ? params : undefined;
    const note = cell.note;

    const delayFx = cell.fx?.find(fx => fx.kind === "noteDelay");
    const delaySec = delayFx
      ? stepSec * Math.min(0.999, Math.max(0, (delayFx.value & 0xff) / 256))
      : 0;
    const onset0 = stepTime + delaySec;

    let gate = stepSec * 1.02;
    const cut = cell.fx?.find(fx => fx.kind === "cut");
    const openHold =
      isCutOpenHold(cut) || (!cut && this.openHoldDefaultSlots[slot] === true);
    if (cut && !openHold) {
      // Cxx: xx/16 steps of gate (0x10 ≈ one 16th step; 0xF0 ≈ nearly a bar).
      gate = stepSec * Math.max(0.05, Math.min(16, cut.value / 16));
    } else if (params.portaSemiPerSec) {
      gate = stepSec * 1.5;
    }

    const retrig = cell.fx?.find(fx => fx.kind === "retrigger");
    const hitsNibble = retrig ? retrig.value & 0x0f : 0;
    const decayNibble = retrig ? (retrig.value >> 4) & 0x0f : 0;
    // Rxy: lo = hit count (0 → 1), hi = per-hit volume decay nibble.
    const hits = retrig ? Math.max(1, hitsNibble === 0 ? 1 : hitsNibble) : 1;

    const gateFx = cell.fx?.find(fx => fx.kind === "gate");
    const slicesNibble = gateFx ? gateFx.value & 0x0f : 0;
    const dutyNibble = gateFx ? (gateFx.value >> 4) & 0x0f : 0;
    // Gxy: lo = slice count (0 → 1), hi = open duty (0 → 50%, else hi/16).
    const slices = gateFx ? Math.max(1, slicesNibble === 0 ? 1 : slicesNibble) : 1;

    const sliceWindow = cut && !openHold
      ? gate
      : Math.max(stepSec * 0.05, stepSec - delaySec);

    // New pitched note replaces any prior unbounded gate on this slot.
    this.clearContinuousGate(slot);

    if (hits > 1) {
      // Ratchet: evenly spaced hits inside the remaining step (or cut window).
      // Pair with hardRetrigger so each hit restarts amp from 0 — without that,
      // soft legato mid-step just “squishes” sustain (plain R04 vs C50R04).
      const interval = sliceWindow / hits;
      // Short staccato pulses leave a gap so attacks read clearly.
      const hitGate = Math.max(0.006, interval * 0.45);
      const decayFactor = decayNibble > 0 ? (16 - decayNibble) / 16 : 1;

      for (let i = 0; i < hits; i++) {
        const t = onset0 + i * interval;
        const vel = Math.max(0.02, velocity * Math.pow(decayFactor, i));
        this.scheduled.add(`${keyBase}:on:${i}`);
        // Every hit gets the full kernel FX (filter, vibrato, arp, slide, …).
        // hardRetrigger resets noteFx state from params — omitting them on later
        // hits would wipe F/V/A/U and leave only a bare amp restart.
        const hitFx = {
          ...(noteFx ?? {}),
          hardRetrigger: true as const,
        };
        this.host.noteOn(slot, note, vel, t, hitFx);
        const offTime = t + hitGate;
        this.scheduled.add(`${keyBase}:gate:${i}`);
        this.host.noteOff(slot, note, offTime);
        this.gateUntil[slot] = offTime;
      }
      return;
    }

    if (slices > 1) {
      // Gate / stutter: one continuous note, chop level via velocity.
      // Domain: Cxx → finite cut window; C-- → unbounded (phase = t % period).
      // Unlike R, amp/mod envelopes and kernel FX keep running — only velocity gates.
      const interval = sliceWindow / slices;
      const duty = dutyNibble > 0 ? dutyNibble / 16 : 0.5;
      const onDur = Math.max(0.004, interval * Math.min(0.92, Math.max(0.08, duty)));

      this.scheduled.add(`${keyBase}:on`);
      this.host.noteOn(slot, note, velocity, onset0, noteFx);

      if (openHold) {
        // Unbounded: keep chopping for the note lifetime (until --- / next note).
        // Filled in scheduleAhead after the row pass so --- can set endAt first.
        this.continuousGates[slot] = {
          onset: onset0,
          period: interval,
          onDur,
          velocity,
          nextIndex: 0,
          keyPrefix: `${keyBase}:cg`,
        };
        this.gateUntil[slot] = undefined;
        return;
      }

      for (let i = 0; i < slices; i++) {
        const tOn = onset0 + i * interval;
        const tMute = tOn + onDur;
        if (i > 0) {
          this.scheduled.add(`${keyBase}:gopen:${i}`);
          this.host.setNoteFx(slot, {}, tOn, velocity);
        }
        this.scheduled.add(`${keyBase}:gmute:${i}`);
        this.host.setNoteFx(slot, {}, tMute, 0);
      }

      const offTime = onset0 + sliceWindow;
      this.scheduled.add(`${keyBase}:gate`);
      this.host.noteOff(slot, note, offTime);
      this.gateUntil[slot] = offTime;
      return;
    }

    const keyOn = `${keyBase}:on`;
    this.scheduled.add(keyOn);
    this.host.noteOn(slot, note, velocity, onset0, noteFx);
    if (openHold) {
      // C--: no host gate — hold until next note / --- / envelope end.
      this.gateUntil[slot] = undefined;
      return;
    }
    const offTime = onset0 + gate;
    this.scheduled.add(`${keyBase}:gate`);
    this.host.noteOff(slot, note, offTime);
    this.gateUntil[slot] = offTime;
  }

  private onAudibilityChanged(): void {
    if (this.playing) {
      this.resyncLookahead();
      return;
    }
    this.applyAudibility();
  }

  /**
   * Cancel future kernel events and rebuild the schedule from the next step.
   * Keeps currently sounding audible voices (and re-posts their gate noteOffs).
   */
  private resyncLookahead(): void {
    if (!this.clip || !this.playing) {
      return;
    }
    const now = this.now();
    const playhead = this.getPlayhead();
    const pattern = playhead
      ? this.clip.patterns.find(entry => entry.id === playhead.patternId)
      : this.clip.patterns[0];

    this.host.cancelPending(now);
    this.scheduled.clear();

    for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
      const s = slot as InstrumentSlot;
      const patternMuted = pattern?.tracks[slot]?.muted === true;
      const audible = this.isTrackAudible(s, patternMuted);
      // Pattern mute always ends open holds (C--), even when solo would still
      // treat the track as "audible" for new notes.
      if (!audible || patternMuted) {
        // ASAP — do not queue a timed closer after cancelPending.
        this.host.noteOff(s, undefined, 0);
        this.gateUntil[slot] = undefined;
        this.clearContinuousGate(s);
        continue;
      }
      const gate = this.gateUntil[slot];
      if (gate != null && gate > now) {
        // cancelPending dropped the original closer — restore it.
        this.host.noteOff(s, undefined, gate);
      }
    }

    const stepSec = secondsPerStep(this.clip);
    if (stepSec > 0) {
      const elapsed = Math.max(0, now - this.origin);
      this.nextStepIndex = Math.floor(elapsed / stepSec) + 1;
    }
    this.lastScheduledPatternId = playhead?.patternId;
    this.scheduleAhead();
  }

  /** Silence slots that just became inaudible (stopped / idle path). */
  private applyAudibility(): void {
    const now = this.host.getCurrentTime();
    const playhead = this.getPlayhead();
    const pattern = playhead && this.clip
      ? this.clip.patterns.find(entry => entry.id === playhead.patternId)
      : this.clip?.patterns[0];
    this.cutInaudibleAt(now, pattern);
  }

  /** Note-off slots that are muted/soloed out for the given pattern. */
  private cutInaudibleAt(
    atTime: number,
    pattern: ClipIR["patterns"][0] | undefined,
  ): void {
    for (let slot = 0; slot < MAX_INSTRUMENT_SLOT_COUNT; slot++) {
      const patternMuted = pattern?.tracks[slot]?.muted === true;
      const audible = this.isTrackAudible(slot as InstrumentSlot, patternMuted);
      // Pattern mute ends holds even when solo overrides audibility.
      if (audible && !patternMuted) {
        continue;
      }
      this.host.noteOff(slot as InstrumentSlot, undefined, atTime);
      this.gateUntil[slot] = undefined;
      this.clearContinuousGate(slot as InstrumentSlot);
    }
  }
}

/** Compact 64-step chip groove for the lab (cuts, vibrato, slides, porta, filter). */
export function createDemoClip(): ClipIR {
  const clip = createEmptyClip({ bpm: 128, steps: 64, stepDivision: 16 });
  const [bass, lead, kick, hat] = clip.patterns[0]!.tracks;

  const n = (name: string) => parseNoteName(name)!;
  const put = (
    track: NonNullable<typeof bass>,
    step: number,
    note: string,
    volHex: number,
    fx: NoteFx[] = [],
  ): void => {
    track.cells[step] = {
      note: n(note),
      volume: volHex / 255,
      ...(fx.length > 0 ? { fx } : {}),
    };
  };
  const cut = (value: number): NoteFx => ({ kind: "cut", value });
  const vib = (value: number): NoteFx => ({ kind: "vibrato", value });
  const up = (value: number): NoteFx => ({ kind: "slideUp", value });
  const down = (value: number): NoteFx => ({ kind: "slideDown", value });
  const porta = (value: number): NoteFx => ({ kind: "portamento", value });
  const filt = (value: number): NoteFx => ({ kind: "filter", value });

  // --- Kick: punchy fours + pickups; short gates via Cxx ---
  for (let bar = 0; bar < 4; bar++) {
    const o = bar * 16;
    put(kick, o + 0, "C-2", 0xff, [cut(0x5)]);
    put(kick, o + 4, "C-2", 0xf0, [cut(0x4)]);
    put(kick, o + 8, "C-2", 0xff, [cut(0x5)]);
    put(kick, o + 12, "C-2", 0xe8, [cut(0x4)]);
    if (bar === 1 || bar === 3) {
      put(kick, o + 14, "C-2", 0xb0, [cut(0x3)]);
    }
    if (bar === 2) {
      put(kick, o + 6, "C-2", 0xc0, [cut(0x3)]);
      put(kick, o + 10, "C-2", 0xa8, [cut(0x3)]);
    }
  }

  // --- Hats: busy 8ths with ghost notes + open-ish hits ---
  for (let step = 0; step < 64; step++) {
    const beat = step % 4;
    if (beat === 0) {
      put(hat, step, "F-4", 0x88, [cut(0x2)]);
    } else if (beat === 2) {
      put(hat, step, "F-4", 0x58, [cut(0x2)]);
    } else if (step % 8 === 3 || step % 8 === 7) {
      put(hat, step, "F-4", 0x38, [cut(0x1)]);
    }
  }
  for (const step of [15, 31, 47, 55, 63]) {
    put(hat, step, "G-4", 0xb0, [cut(0x8)]);
  }
  for (const step of [42, 43, 44, 45, 46]) {
    put(hat, step, "F-4", 0x70 - (step - 42) * 8, [cut(0x1)]);
  }

  // --- Bass: syncopated Cm riff + filter/slide ear-candy ---
  const bassLine: Array<[number, string, number, NoteFx[]]> = [
    [0, "C-2", 0x98, [cut(0x7), filt(0xA0)]],
    [2, "C-2", 0x50, [cut(0x3)]],
    [3, "D#2", 0x60, [cut(0x3), up(0x28)]],
    [4, "G-1", 0x88, [cut(0x6)]],
    [6, "G-1", 0x48, [cut(0x3)]],
    [8, "A#1", 0x95, [cut(0x7), filt(0x90)]],
    [10, "A#1", 0x45, [cut(0x3)]],
    [11, "G-1", 0x55, [cut(0x3)]],
    [12, "F-1", 0x90, [cut(0x7)]],
    [14, "D#2", 0x70, [cut(0x8), down(0x38)]],
    [16, "C-2", 0x9a, [cut(0x7), filt(0xB0)]],
    [18, "C-2", 0x48, [cut(0x3)]],
    [20, "G-1", 0x85, [cut(0x6)]],
    [22, "A#1", 0x68, [cut(0x4), up(0x20)]],
    [24, "A#1", 0x92, [cut(0x7)]],
    [26, "A#1", 0x40, [cut(0x2)]],
    [27, "C-2", 0x58, [cut(0x3)]],
    [28, "D#2", 0x8a, [cut(0x6), filt(0x70)]],
    [30, "F-1", 0x78, [cut(0x5)]],
    [32, "G-2", 0x90, [cut(0x6), filt(0xC0)]],
    [34, "G-2", 0x48, [cut(0x3)]],
    [35, "A#2", 0x55, [cut(0x3), up(0x18)]],
    [36, "C-2", 0x88, [cut(0x7)]],
    [38, "D#2", 0x70, [cut(0x4)]],
    [40, "F-2", 0x92, [cut(0x6)]],
    [42, "F-2", 0x45, [cut(0x2)]],
    [43, "D#2", 0x58, [cut(0x3), down(0x20)]],
    [44, "C-2", 0x95, [cut(0x7), filt(0x88)]],
    [46, "A#1", 0x6a, [cut(0x4)]],
    [48, "C-2", 0x9c, [cut(0x7)]],
    [50, "C-2", 0x50, [cut(0x3)]],
    [51, "D#2", 0x62, [cut(0x3)]],
    [52, "G-1", 0x88, [cut(0x6)]],
    [54, "G-1", 0x4a, [cut(0x3)]],
    [56, "A#1", 0x90, [cut(0x6), filt(0xA8)]],
    [58, "C-2", 0x78, [cut(0x4)]],
    [59, "D#2", 0x60, [cut(0x3)]],
    [60, "F-1", 0x98, [cut(0x8), down(0x30)]],
    [62, "G-1", 0x70, [cut(0x4)]],
    [63, "A#1", 0x55, [cut(0x2)]],
  ];
  for (const [step, note, vol, fx] of bassLine) {
    put(bass, step, note, vol, fx);
  }

  // --- Lead: arps, vibrato holds, porta fall ---
  const arp = ["C-4", "D#4", "G-4", "C-5", "G-4", "D#4"] as const;
  for (let i = 0; i < 12; i++) {
    put(lead, i, arp[i % arp.length]!, 0x78 - (i % 3) * 10, [cut(0x3)]);
  }
  for (const [step, note, vol] of [
    [16, "A#3", 0x70],
    [18, "C-4", 0x78],
    [20, "D#4", 0x80],
    [22, "G-4", 0x88],
    [24, "A#4", 0x70],
    [26, "G-4", 0x60],
    [28, "D#4", 0x78],
    [30, "C-4", 0x55],
  ] as const) {
    put(lead, step, note, vol, [cut(0x4), ...(step === 24 ? [vib(0x68)] : [])]);
  }
  // Sparse hooks with vibrato + filter open
  put(lead, 33, "G-4", 0x90, [cut(0xC), vib(0x7A)]);
  put(lead, 37, "A#4", 0x80, [cut(0x8), vib(0x59), filt(0xC8)]);
  put(lead, 41, "C-5", 0x98, [cut(0xA), vib(0x8B)]);
  put(lead, 45, "A#4", 0x70, [cut(0x5), down(0x22)]);
  put(lead, 47, "G-4", 0x58, [cut(0x3)]);
  // Cascading finish with portamento into the low answer
  put(lead, 48, "C-5", 0x88, [cut(0x4)]);
  put(lead, 50, "A#4", 0x82, [porta(0x55)]);
  put(lead, 52, "G-4", 0x7a, [porta(0x48)]);
  put(lead, 54, "F-4", 0x70, [porta(0x40)]);
  put(lead, 56, "D#4", 0x68, [porta(0x38), vib(0x46)]);
  put(lead, 58, "C-4", 0x60, [porta(0x30)]);
  put(lead, 60, "A#3", 0x58, [porta(0x28)]);
  put(lead, 62, "G-3", 0x50, [porta(0x20), cut(0x8)]);

  return clip;
}

/** Tasteful post-mix chain for the demo groove. */
export function createDemoFxStack(): FxNode[] {
  return [
    normalizeFxNode({
      type: "chorus",
      wet: 0.22,
      params: { rateHz: 0.85, depthMs: 2.6, delayMs: 7 },
    }),
    normalizeFxNode({
      type: "reverb",
      wet: 0.26,
      params: { room: 0.42, damp: 0.55 },
    }),
  ];
}
